import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { Text, Title } = Typography;

const STATUS_FILTERS = [
  { value: "all", label: "All Statuses" },
  { value: "needs_review", label: "Needs Review" },
  { value: "ready_to_create", label: "Ready to Create" },
  { value: "created", label: "Created" },
];

const STORAGE_TYPES = ["Non Ottoman", "Ottoman", "Cloud Storage"];
const DEFAULT_VAT_RATE = 0.23;
const AUTO_SYNC_INTERVAL_MS = 15 * 60 * 1000;

const DEFAULT_OPTIONS = {
  customers: [],
  products: [],
  sizes: [],
  fabrics: [],
  fabricColors: [],
  storageTypes: STORAGE_TYPES,
};

const DEFAULT_PRICE_PREVIEW = {
  loading: false,
  items: [],
  summary: null,
  error: "",
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const toOptionLabel = (...parts) => parts.filter(Boolean).join(" — ");

const getCustomerDisplayName = (customer) =>
  customer?.company_name ||
  customer?.full_name ||
  customer?.name ||
  customer?.email ||
  `Customer #${customer?.id}`;

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "€0.00";
  return `€${n.toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

const formatDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const addDaysInput = (value, days = 14) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + Number(days || 0));
  return date.toISOString().slice(0, 10);
};

const getOrderSortTime = (order) => {
  const candidates = [
    order?.order_date,
    order?.orderDate,
    order?.email_date,
    order?.emailDate,
    order?.createdAt,
    order?.created_at,
    order?.updatedAt,
    order?.updated_at,
  ];

  for (const value of candidates) {
    if (!value) continue;
    const time = new Date(value).getTime();
    if (Number.isFinite(time)) return time;
  }

  return 0;
};

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeCompact = (value) => normalizeText(value).replace(/\s+/g, "");

const getTokens = (value) =>
  normalizeText(value)
    .split(" ")
    .filter((token) => token.length >= 2);

const cleanExtractedProduct = (value) =>
  String(value || "")
    .replace(/\bSize\s*:?.*$/i, "")
    .replace(/\bFabric\s*:?.*$/i, "")
    .replace(/\bColour\s*:?.*$/i, "")
    .replace(/\bColor\s*:?.*$/i, "")
    .replace(/\bStorage\s*Type\s*:?.*$/i, "")
    .replace(/\bQuantity\s*:?.*$/i, "")
    .trim();

const cleanExtractedSize = (value) =>
  String(value || "")
    .replace(/\bFabric\s*:?.*$/i, "")
    .replace(/\bColour\s*:?.*$/i, "")
    .replace(/\bColor\s*:?.*$/i, "")
    .replace(/\bStorage\s*Type\s*:?.*$/i, "")
    .replace(/\bQuantity\s*:?.*$/i, "")
    .trim();

const cleanExtractedFabric = (value) =>
  String(value || "")
    .replace(/\bColour\s*:?.*$/i, "")
    .replace(/\bColor\s*:?.*$/i, "")
    .replace(/\bStorage\s*Type\s*:?.*$/i, "")
    .replace(/\bQuantity\s*:?.*$/i, "")
    .trim();

const getBestMatchId = (rows, query, labelGetter, minScore = 0.45) => {
  const cleanQuery = normalizeText(query);
  if (!cleanQuery) return undefined;

  let best = null;

  for (const row of safeArray(rows)) {
    const label = normalizeText(labelGetter(row));
    if (!label) continue;

    if (
      label === cleanQuery ||
      cleanQuery.includes(label) ||
      label.includes(cleanQuery)
    ) {
      return row.id;
    }

    const labelTokens = getTokens(label);
    if (!labelTokens.length) continue;

    const matchedTokens = labelTokens.filter((token) =>
      cleanQuery.includes(token)
    );

    const score = matchedTokens.length / Math.max(labelTokens.length, 1);

    if (!best || score > best.score) {
      best = { id: row.id, score };
    }
  }

  return best && best.score >= minScore ? best.id : undefined;
};

const findProductById = (products, id) =>
  safeArray(products).find((product) => Number(product.id) === Number(id));

const isBedProductName = (productName = "") => {
  const name = normalizeText(productName);

  if (!name) return false;

  if (
    name.includes("mattress") ||
    name.includes("pillow") ||
    name.includes("protector") ||
    name.includes("sheet") ||
    name.includes("duvet")
  ) {
    return false;
  }

  return (
    name.includes("bed") ||
    name.includes("ottoman") ||
    name.includes("frame") ||
    name.includes("headboard") ||
    name.includes("divan")
  );
};

const guessStorageType = (text = "") => {
  const clean = normalizeText(text);

  if (clean.includes("cloud storage")) return "Cloud Storage";
  if (clean.includes("non ottoman") || clean.includes("no storage")) {
    return "Non Ottoman";
  }
  if (clean.includes("ottoman")) return "Ottoman";

  return undefined;
};

const getSizeAliasTarget = (query) => {
  const clean = normalizeText(query);

  if (
    clean.includes("6ft") ||
    clean.includes("6 ft") ||
    clean.includes("super king")
  ) {
    return "super king";
  }

  if (clean.includes("5ft") || clean.includes("5 ft") || clean === "king") {
    return "king";
  }

  if (
    clean.includes("4ft6") ||
    clean.includes("4 ft 6") ||
    clean.includes("double")
  ) {
    return "double";
  }

  if (
    clean.includes("4ft") ||
    clean.includes("4 ft") ||
    clean.includes("small double")
  ) {
    return "small double";
  }

  if (
    clean.includes("3ft") ||
    clean.includes("3 ft") ||
    clean === "single"
  ) {
    return "single";
  }

  if (clean.includes("90x200") || clean.includes("single euro")) {
    return "single euro";
  }

  if (clean.includes("90x190")) {
    return "single";
  }

  return "";
};

const findSizeByAlias = (sizes, query) => {
  const target = getSizeAliasTarget(query);
  if (!target) return undefined;

  const normalizedTarget = normalizeText(target);

  const exact = safeArray(sizes).find(
    (size) => normalizeText(size.size) === normalizedTarget
  );

  if (exact) return exact.id;

  const contains = safeArray(sizes).find((size) =>
    normalizeText(size.size).includes(normalizedTarget)
  );

  return contains?.id;
};

const guessProductId = (products, item) => {
  const query = [
    cleanExtractedProduct(item?.raw_product_text),
    cleanExtractedProduct(item?.raw_description),
  ]
    .filter(Boolean)
    .join(" ");

  return getBestMatchId(products, query, (product) => product.product_name, 0.35);
};

const guessSizeId = (sizes, item) => {
  const query = [
    cleanExtractedSize(item?.raw_size_text),
    cleanExtractedProduct(item?.raw_product_text),
    item?.raw_description,
  ]
    .filter(Boolean)
    .join(" ");

  const aliasMatch = findSizeByAlias(sizes, query);
  if (aliasMatch) return aliasMatch;

  return getBestMatchId(sizes, query, (size) => size.size, 0.45);
};

const guessFabricId = (fabrics, item) => {
  const query = [
    cleanExtractedFabric(item?.raw_fabric_text),
    item?.raw_description,
  ]
    .filter(Boolean)
    .join(" ");

  return getBestMatchId(fabrics, query, (fabric) => fabric.fabric_name, 0.45);
};

const guessFabricColorId = (fabricColors, item) => {
  const query = [
    cleanExtractedFabric(item?.raw_fabric_text),
    item?.raw_description,
  ]
    .filter(Boolean)
    .join(" ");

  return getBestMatchId(fabricColors, query, (color) => color.color_name, 0.45);
};

const extractCompanyFromRawText = (text) => {
  const match = String(text || "").match(
    /(?:Customer\s*Company\s*Name|Company\s*Name|Customer\s*Name)\s*[:#]?\s*([^\n\r]+)/i
  );

  return match?.[1] ? match[1].trim() : "";
};

const extractRegisteredEmailFromRawText = (text) => {
  const match = String(text || "").match(
    /(?:Registered\s*Portal\s*Email|Portal\s*Email|Registered\s*Email|Customer\s*Email)\s*[:#]?\s*([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i
  );

  return match?.[1] ? match[1].trim().toLowerCase() : "";
};

const guessCustomerId = (customers, order) => {
  if (order?.matched_user_id) return order.matched_user_id;

  const parsed = order?.parsed_data || {};

  const registeredEmail =
    parsed.registered_portal_email ||
    extractRegisteredEmailFromRawText(order?.raw_text);

  const fromEmail = String(order?.from_email || "").toLowerCase().trim();

  const companyName =
    parsed.company_name || extractCompanyFromRawText(order?.raw_text);

  if (registeredEmail) {
    const exactEmailUser = safeArray(customers).find(
      (customer) =>
        String(customer.email || "").toLowerCase().trim() === registeredEmail
    );

    if (exactEmailUser) return exactEmailUser.id;
  }

  if (fromEmail) {
    const exactSenderUser = safeArray(customers).find(
      (customer) =>
        String(customer.email || "").toLowerCase().trim() === fromEmail
    );

    if (exactSenderUser) return exactSenderUser.id;
  }

  if (companyName) {
    const companyCompact = normalizeCompact(companyName);

    const exactCompanyUser = safeArray(customers).find(
      (customer) => normalizeCompact(customer.company_name) === companyCompact
    );

    if (exactCompanyUser) return exactCompanyUser.id;

    const containsCompanyUser = safeArray(customers).find((customer) => {
      const userCompany = normalizeCompact(customer.company_name);

      return (
        userCompany &&
        companyCompact &&
        (userCompany.includes(companyCompact) ||
          companyCompact.includes(userCompany))
      );
    });

    if (containsCompanyUser) return containsCompanyUser.id;
  }

  return undefined;
};

const extractSenderDomain = (order) => {
  const parsedDomain = order?.parsed_data?.sender_domain;
  if (parsedDomain) return parsedDomain;

  const email = String(order?.from_email || "").toLowerCase();
  const parts = email.split("@");
  return parts.length === 2 ? parts[1] : "";
};

const getPreviewItemPrice = (previewItems, index, itemId) => {
  const rows = safeArray(previewItems);

  const byId = rows.find((row) => {
    const ids = [
      row.itemId,
      row.inputId,
      row.email_order_import_item_id,
      row.id,
    ]
      .filter((value) => value !== undefined && value !== null)
      .map((value) => Number(value));

    return ids.includes(Number(itemId));
  });

  if (byId) return byId;

  return rows[index] || null;
};

const pickPriceValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
  }

  return null;
};

const normalizePreviewSummary = (data) => {
  const result = data?.result || data?.preview || data?.data || data || {};

  return (
    result.summary ||
    result.totals ||
    data?.summary ||
    data?.totals ||
    {
      subtotalExVat:
        result.subtotalExVat ||
        result.subtotal_ex_vat ||
        result.subtotal ||
        0,
      vatAmount:
        result.vatAmount ||
        result.vat_amount ||
        result.taxAmount ||
        result.tax_amount ||
        0,
      totalWithVat:
        result.totalWithVat ||
        result.total_with_vat ||
        result.total ||
        0,
    }
  );
};

const getLineMoney = (preview, ...keys) => {
  if (!preview) return 0;

  for (const key of keys) {
    const value = preview[key];

    if (value !== undefined && value !== null && value !== "") {
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
  }

  return 0;
};

const getLinePricingValues = (preview) => {
  return {
    ok: preview?.ok !== false,
    message: preview?.message || "",

    unitExVat: getLineMoney(
      preview,
      "unitPriceExVat",
      "unit_price_ex_vat",
      "serverUnitPriceExVat",
      "server_unit_price_ex_vat"
    ),

    vat: getLineMoney(preview, "unitVat", "unit_vat"),

    unitInclVat: getLineMoney(
      preview,
      "unitPriceWithVat",
      "unit_price_with_vat"
    ),

    lineTotal: getLineMoney(
      preview,
      "lineTotalWithVat",
      "line_total_with_vat",
      "lineTotal",
      "line_total"
    ),


    baseSalePrice: getLineMoney(preview, "baseSalePrice", "base_sale_price"),
    fabricSalePrice: getLineMoney(preview, "fabricSalePrice", "fabric_sale_price"),
    storageSalePrice: getLineMoney(preview, "storageSalePrice", "storage_sale_price"),
  };
};

const AutoOrdersAdmin = () => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerIdFilter, setCustomerIdFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const [options, setOptions] = useState(DEFAULT_OPTIONS);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pricePreview, setPricePreview] = useState(DEFAULT_PRICE_PREVIEW);

  const pricePreviewTimerRef = useRef(null);
  const pricePreviewUpdatingRef = useRef(false);
  const syncInProgressRef = useRef(false);
  const autoSyncTimerRef = useRef(null);

  const [form] = Form.useForm();
  const watchedItems = Form.useWatch("items", form) || [];
  const watchedCustomerId = Form.useWatch("matched_user_id", form);

  const fetchOptions = async ({ force = false } = {}) => {
    try {
      if (
        !force &&
        options.customers.length &&
        options.products.length &&
        options.sizes.length
      ) {
        return options;
      }

      const response = await axiosInstance.get("/admin/auto-orders/review-options");

      if (response.data?.success) {
        const nextOptions = {
          customers: response.data.customers || [],
          products: response.data.products || [],
          sizes: response.data.sizes || [],
          fabrics: response.data.fabrics || [],
          fabricColors: response.data.fabricColors || [],
          storageTypes: response.data.storageTypes || STORAGE_TYPES,
        };

        setOptions(nextOptions);
        return nextOptions;
      }

      return options;
    } catch (error) {
      console.error("AUTO ORDER OPTIONS ERROR:", error.response?.data || error);
      message.error(error.response?.data?.message || "Failed to load review options");
      return options;
    }
  };

  const fetchOrders = async ({
    status = statusFilter,
    customerId = customerIdFilter,
    nextPage = page,
    searchText = search,
  } = {}) => {
    try {
      setLoading(true);

      const response = await axiosInstance.get("/admin/auto-orders", {
        params: {
          status: status && status !== "all" ? status : undefined,
          customerId: customerId && customerId !== "all" ? customerId : undefined,
          page: nextPage,
          limit: 20,
          search: searchText || undefined,
        },
      });

      if (response.data?.success) {
        setOrders(response.data.orders || []);
        setTotal(response.data.total || 0);
        setPage(response.data.page || nextPage);
      }
    } catch (error) {
      console.error("AUTO ORDER FETCH ERROR:", error.response?.data || error);
      message.error(error.response?.data?.message || "Failed to fetch auto orders");
    } finally {
      setLoading(false);
    }
  };

  const buildItemFormValue = (item, order, reviewOptions) => {
    const cleanedProductText = cleanExtractedProduct(item?.raw_product_text);
    const cleanedDescription = cleanExtractedProduct(item?.raw_description);

    const productId =
      item.productId ||
      guessProductId(reviewOptions.products, {
        ...item,
        raw_product_text: cleanedProductText,
        raw_description: cleanedDescription,
      });

    const selectedProduct = findProductById(reviewOptions.products, productId);

    const isBedItem = isBedProductName(
      selectedProduct?.product_name || cleanedProductText || cleanedDescription
    );

    const guessedStorageType =
      item.storageType ||
      guessStorageType(`${item?.raw_product_text || ""} ${item?.raw_description || ""}`);

    return {
      id: item.id,
      productId: productId || undefined,

      sizeId:
        item.sizeId ||
        guessSizeId(reviewOptions.sizes, {
          ...item,
          raw_size_text: cleanExtractedSize(item?.raw_size_text),
          raw_product_text: cleanedProductText,
          raw_description: cleanedDescription,
        }) ||
        undefined,

      fabricId: isBedItem
        ? item.fabricId ||
          guessFabricId(reviewOptions.fabrics, {
            ...item,
            raw_fabric_text: cleanExtractedFabric(item?.raw_fabric_text),
            raw_description: cleanedDescription,
          }) ||
          undefined
        : undefined,

      fabricColorId: isBedItem
        ? item.fabricColorId ||
          guessFabricColorId(reviewOptions.fabricColors, {
            ...item,
            raw_fabric_text: cleanExtractedFabric(item?.raw_fabric_text),
            raw_description: cleanedDescription,
          }) ||
          undefined
        : undefined,

      storageType: isBedItem ? guessedStorageType || undefined : undefined,

      quantity: item.quantity || 1,

      confirmed_sale_unit_price:
        item.confirmed_sale_unit_price === null ||
        item.confirmed_sale_unit_price === undefined
          ? undefined
          : Number(item.confirmed_sale_unit_price),


      admin_notes: item.admin_notes || "",
    };
  };

  const fetchDetails = async (id) => {
    try {
      const reviewOptions = await fetchOptions();

      const response = await axiosInstance.get(`/admin/auto-orders/${id}`);

      if (!response.data?.success) return;

      const order = response.data.order;
      setSelectedOrder(order);
      setPricePreview(DEFAULT_PRICE_PREVIEW);

      form.setFieldsValue({
        matched_user_id:
          order.matched_user_id ||
          guessCustomerId(reviewOptions.customers, order) ||
          undefined,
        purchase_order_no: order.purchase_order_no || "",
        order_date: formatDateInput(order.order_date || order.email_date),
        deliver_by:
          formatDateInput(order.deliver_by) ||
          addDaysInput(order.order_date || order.email_date, 14),
        review_notes: order.review_notes || "",
        items: safeArray(order.items).map((item) =>
          buildItemFormValue(item, order, reviewOptions)
        ),
      });

      setDetailsOpen(true);
    } catch (error) {
      console.error("AUTO ORDER DETAILS ERROR:", error.response?.data || error);
      message.error(error.response?.data?.message || "Failed to fetch order details");
    }
  };

  const applyPricePreviewToForm = (previewItems) => {
    const currentItems = safeArray(form.getFieldValue("items"));
    if (!currentItems.length) return;

    let changed = false;

    const updatedItems = currentItems.map((item, index) => {
      const preview = getPreviewItemPrice(previewItems, index, item?.id);

      if (!preview || preview.ok === false) return item;

      const unitSalePrice = pickPriceValue(
        preview.unitPriceExVat,
        preview.unit_price_ex_vat,
        preview.serverUnitPriceExVat,
        preview.server_unit_price_ex_vat,
        preview.unitPrice,
        preview.unit_price,
        preview.confirmed_sale_unit_price
      );

      const nextItem = { ...item };

      if (
        unitSalePrice !== null &&
        Number(item.confirmed_sale_unit_price) !== Number(unitSalePrice)
      ) {
        nextItem.confirmed_sale_unit_price = unitSalePrice;
        changed = true;
      }


      return nextItem;
    });

    if (changed) {
      pricePreviewUpdatingRef.current = true;
      form.setFieldsValue({ items: updatedItems });

      setTimeout(() => {
        pricePreviewUpdatingRef.current = false;
      }, 0);
    }
  };

  const requestPricePreview = async () => {
    if (!selectedOrder?.id || !detailsOpen) return;

    const values = form.getFieldsValue(true);
    const formItems = safeArray(values.items);

    const validItems = formItems
      .map((item) => {
        const selectedProduct = findProductById(options.products, item.productId);
        const isBedItem =
          selectedProduct?.isBed === true ||
          isBedProductName(selectedProduct?.product_name || "");

        return {
          id: item.id,
          itemId: item.id,

          productId: item.productId ? Number(item.productId) : null,
          sizeId: item.sizeId ? Number(item.sizeId) : null,
          quantity: item.quantity ? Number(item.quantity) : 1,

          storageType: isBedItem ? item.storageType || null : null,
          fabricId: isBedItem && item.fabricId ? Number(item.fabricId) : null,
          fabricColorId:
            isBedItem && item.fabricColorId ? Number(item.fabricColorId) : null,

          // Do not send confirmed_sale_unit_price to preview.
          // Preview must always recalculate from product/size/fabric/color/storage,
          // otherwise old saved values can lock the price incorrectly.

        };
      })
      .filter((item) => item.productId && item.sizeId && item.quantity > 0);

    if (!validItems.length) {
      setPricePreview({
        loading: false,
        items: [],
        summary: null,
        error: "",
      });
      return;
    }

    try {
      setPricePreview((prev) => ({
        ...prev,
        loading: true,
        error: "",
      }));

      const response = await axiosInstance.post("/admin/auto-orders/price-preview", {
        autoOrderId: selectedOrder.id,
        importId: selectedOrder.id,
        matched_user_id: values.matched_user_id || null,
        customerId: values.matched_user_id || null,
        userId: values.matched_user_id || null,
        vatRate: DEFAULT_VAT_RATE,
        items: validItems,
      });

      const data = response.data || {};
      const result = data.result || data.preview || data.data || data;

      const previewItems =
        result.items ||
        result.lines ||
        result.previewItems ||
        data.items ||
        data.lines ||
        [];

      const summary = normalizePreviewSummary(data);

      setPricePreview({
        loading: false,
        items: safeArray(previewItems),
        summary: summary || null,
        error: "",
      });

      applyPricePreviewToForm(previewItems);
    } catch (error) {
      console.error("AUTO ORDER PRICE PREVIEW ERROR:", error.response?.data || error);

      setPricePreview({
        loading: false,
        items: [],
        summary: null,
        error:
          error.response?.data?.message ||
          error.message ||
          "Price preview failed",
      });
    }
  };

  useEffect(() => {
    if (!detailsOpen || !selectedOrder?.id) return;
    if (pricePreviewUpdatingRef.current) return;

    if (pricePreviewTimerRef.current) {
      clearTimeout(pricePreviewTimerRef.current);
    }

    pricePreviewTimerRef.current = setTimeout(() => {
      requestPricePreview();
    }, 500);

    return () => {
      if (pricePreviewTimerRef.current) {
        clearTimeout(pricePreviewTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    detailsOpen,
    selectedOrder?.id,
    watchedCustomerId,
    JSON.stringify(watchedItems),
    options.products,
  ]);

  const syncEmails = async ({ silent = false } = {}) => {
    if (syncInProgressRef.current) {
      if (!silent) {
        message.info("Email sync is already running.");
      }
      return null;
    }

    syncInProgressRef.current = true;

    try {
      if (!silent) {
        setSyncing(true);
      }

      const response = await axiosInstance.post("/admin/auto-orders/sync", {
        unseenOnly: false,
      });

      console.log(
        silent ? "AUTO ORDER AUTO SYNC RESPONSE:" : "AUTO ORDER MANUAL SYNC RESPONSE:",
        response.data
      );

      if (response.data?.success) {
        const result = response.data.result || {};
        const importedCount = Number(result.imported || 0);

        if (silent) {
          if (importedCount > 0) {
            message.success(`${importedCount} new order email(s) synced automatically`);
          }
        } else {
          message.success(response.data.message || "Email sync completed");
        }

        await fetchOrders({
          status: statusFilter,
          customerId: customerIdFilter,
          nextPage: 1,
          searchText: search,
        });

        return result;
      }

      return null;
    } catch (error) {
      console.error(
        silent ? "AUTO ORDER AUTO SYNC ERROR:" : "AUTO ORDER MANUAL SYNC ERROR:",
        error.response?.data || error
      );

      if (!silent) {
        message.error(error.response?.data?.message || "Email sync failed");
      }

      return null;
    } finally {
      if (!silent) {
        setSyncing(false);
      }

      syncInProgressRef.current = false;
    }
  };


  const sanitizeOrderValues = (values) => {
    const sanitizedItems = safeArray(values.items).map((item) => {
      const selectedProduct = findProductById(options.products, item.productId);
      const isBedItem = isBedProductName(selectedProduct?.product_name || "");

      if (!isBedItem) {
        return {
          ...item,
          storageType: null,
          fabricId: null,
          fabricColorId: null,
        };
      }

      return item;
    });

    return {
      ...values,
      items: sanitizedItems,
    };
  };

  const saveOrder = async () => {
    if (!selectedOrder?.id) return null;

    try {
      setSaving(true);

      const values = await form.validateFields();
      const payload = sanitizeOrderValues(values);

      const response = await axiosInstance.put(
        `/admin/auto-orders/${selectedOrder.id}`,
        payload
      );

      if (response.data?.success) {
        message.success("Auto order saved successfully");
        setSelectedOrder(response.data.order);

        await fetchOrders({
          status: statusFilter,
          customerId: customerIdFilter,
          nextPage: page,
          searchText: search,
        });

        return response.data.order;
      }

      return null;
    } catch (error) {
      if (error?.errorFields) return null;

      console.error("AUTO ORDER SAVE ERROR:", error.response?.data || error);
      message.error(error.response?.data?.message || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

const createFinalOrder = async () => {
  if (!selectedOrder?.id) {
    message.error("Auto order not selected.");
    return;
  }

  try {
    setCreating(true);

    const saved = await saveOrder();

    if (!saved) {
      message.error("Please complete required fields before confirming.");
      return;
    }

    const response = await axiosInstance.post(
      `/admin/auto-orders/${selectedOrder.id}/create-order`,
      {
        vatRate: DEFAULT_VAT_RATE,
        overrideCreditLimit: false,
      }
    );

    if (response.data?.success) {
      message.success("Final order created successfully");

      setDetailsOpen(false);
      setSelectedOrder(null);
      setPricePreview(DEFAULT_PRICE_PREVIEW);

      await fetchOrders({
        status: statusFilter,
        customerId: customerIdFilter,
        nextPage: 1,
        searchText: search,
      });
    } else {
      message.error(response.data?.message || "Final order creation failed");
    }
  } catch (error) {
    console.error("AUTO ORDER CREATE FINAL ERROR:", error.response?.data || error);

    Modal.error({
      title: "Final Order Creation Failed",
      width: 750,
      zIndex: 7000,
      content: (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            maxHeight: 450,
            overflow: "auto",
            background: "#fff2f0",
            border: "1px solid #ffccc7",
            padding: 12,
            borderRadius: 8,
          }}
        >
          {JSON.stringify(
            error.response?.data || { message: error.message },
            null,
            2
          )}
        </pre>
      ),
    });

    message.error(error.response?.data?.message || "Final order creation failed");
  } finally {
    setCreating(false);
  }
};

  const deleteAutoOrder = (record) => {
    if (!record?.id) return;

    Modal.confirm({
      title: "Remove this import from system?",
      content:
        "This only removes the imported row from this admin screen. It will not delete the original email from mailbox and will not delete any real order already created.",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await axiosInstance.delete(`/admin/auto-orders/${record.id}`);

          if (response.data?.success) {
            message.success(response.data.message || "Auto order removed");

            if (selectedOrder?.id === record.id) {
              setDetailsOpen(false);
              setSelectedOrder(null);
              setPricePreview(DEFAULT_PRICE_PREVIEW);
            }

            await fetchOrders({
              status: statusFilter,
              customerId: customerIdFilter,
              nextPage: 1,
              searchText: search,
            });
          }
        } catch (error) {
          console.error("AUTO ORDER DELETE ERROR:", error.response?.data || error);
          message.error(error.response?.data?.message || "Failed to delete auto order");
        }
      },
    });
  };

  useEffect(() => {
    fetchOptions({ force: true });
    fetchOrders({ status: "all", customerId: "all", nextPage: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    autoSyncTimerRef.current = setInterval(() => {
      syncEmails({ silent: true });
    }, AUTO_SYNC_INTERVAL_MS);

    return () => {
      if (autoSyncTimerRef.current) {
        clearInterval(autoSyncTimerRef.current);
        autoSyncTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, customerIdFilter, search]);

  const customerOptions = useMemo(
    () =>
      options.customers.map((customer) => ({
        value: customer.id,
        label: toOptionLabel(getCustomerDisplayName(customer), customer.email),
      })),
    [options.customers]
  );

  const customerFilterOptions = useMemo(
    () => [
      {
        value: "all",
        label: "All Customers",
      },
      ...safeArray(options.customers).map((customer) => ({
        value: String(customer.id),
        label: getCustomerDisplayName(customer),
      })),
    ],
    [options.customers]
  );

  const productOptions = useMemo(
    () =>
      options.products.map((product) => ({
        value: product.id,
        label: `${product.product_name} (#${product.id})`,
      })),
    [options.products]
  );

  const sizeOptions = useMemo(
    () =>
      options.sizes.map((size) => ({
        value: size.id,
        label: `${size.size} (#${size.id})`,
      })),
    [options.sizes]
  );

  const fabricOptions = useMemo(
    () =>
      options.fabrics.map((fabric) => ({
        value: fabric.id,
        label: `${fabric.fabric_name} (#${fabric.id})`,
      })),
    [options.fabrics]
  );

  const colorOptions = useMemo(
    () =>
      options.fabricColors.map((color) => ({
        value: color.id,
        label: `${color.color_name} (#${color.id})`,
        fabricId: color.fabricId,
      })),
    [options.fabricColors]
  );

  const sortedOrders = useMemo(() => {
    return [...safeArray(orders)].sort((a, b) => {
      const dateDiff = getOrderSortTime(b) - getOrderSortTime(a);
      if (dateDiff !== 0) return dateDiff;
      return Number(b?.id || 0) - Number(a?.id || 0);
    });
  }, [orders]);


  const isBadTableText = (value) => {
    const text = String(value || "").trim();
    const clean = normalizeText(text);

    if (!clean) return true;
    if (text.length > 180) return true;

    const badParts = [
      "hi ",
      "hello",
      "hope this email",
      "kind regards",
      "regards",
      "from ",
      "sent ",
      "subject ",
      "please don t print",
      "warehouse deliveries",
      "collections",
      "caution this email",
      "unsubscribe",
      "cid",
      "mailto",
      "phone",
      "postcode",
      "address",
      "credit reference",
      "credit note",
      "refund",
      "faulty",
      "uplift",
      "complaint",
    ];

    if (badParts.some((part) => clean.includes(part))) return true;

    const hasProductSignal =
      /\b(bed|mattress|ottoman|headboard|frame|base|divan|pillow|protector|sally|vienna|maya|luna|sofia)\b/i.test(
        text
      );

    const hasSizeSignal = /\b(3ft|4ft|4ft6|5ft|6ft|single|double|king|super king|small double)\b/i.test(
      text
    );

    const hasFabricSignal = /\b(plush|steel|naples|tweed|chenille|fabric|colour|color)\b/i.test(
      text
    );

    return !(hasProductSignal || hasSizeSignal || hasFabricSignal);
  };

  const cleanTableText = (value, cleaner = (v) => v) => {
    const cleaned = String(cleaner(value) || "")
      .replace(/[\r\n]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    return isBadTableText(cleaned) ? "" : cleaned;
  };

  const findOptionLabelById = (rows, id, labelKey) => {
    if (!id) return "";
    const row = safeArray(rows).find((item) => Number(item.id) === Number(id));
    return row?.[labelKey] || "";
  };

  const getDisplaySize = (item) => {
    const matchedSize =
      item.size?.size ||
      findOptionLabelById(options.sizes, item.sizeId, "size");

    if (matchedSize) return matchedSize;

    const guessedSizeId = guessSizeId(options.sizes, item);
    const guessedSize = findOptionLabelById(options.sizes, guessedSizeId, "size");

    return guessedSize || cleanTableText(item.raw_size_text, cleanExtractedSize);
  };

  const getDisplayProduct = (item) => {
    const matchedProduct =
      item.product?.product_name ||
      findOptionLabelById(options.products, item.productId, "product_name");

    if (matchedProduct) return matchedProduct;

    const guessedProductId = guessProductId(options.products, item);
    const guessedProduct = findOptionLabelById(
      options.products,
      guessedProductId,
      "product_name"
    );

    if (guessedProduct) return guessedProduct;

    return (
      cleanTableText(item.raw_product_text, cleanExtractedProduct) ||
      cleanTableText(item.raw_description, cleanExtractedProduct)
    );
  };

  const getDisplayFabricColor = (item) => {
    const fabric =
      item.fabric?.fabric_name ||
      findOptionLabelById(options.fabrics, item.fabricId, "fabric_name") ||
      findOptionLabelById(
        options.fabrics,
        guessFabricId(options.fabrics, item),
        "fabric_name"
      );

    const color =
      item.fabricColor?.color_name ||
      findOptionLabelById(options.fabricColors, item.fabricColorId, "color_name") ||
      findOptionLabelById(
        options.fabricColors,
        guessFabricColorId(options.fabricColors, item),
        "color_name"
      );

    const rawFabric = cleanTableText(item.raw_fabric_text, cleanExtractedFabric);

    return [fabric || rawFabric, color].filter(Boolean).join(" / ");
  };

  const itemHasConfirmData = (item) => {
    const product = item.productId || item.product?.id || getDisplayProduct(item);
    const size = item.sizeId || item.size?.id || getDisplaySize(item);
    const qty = Number(item.quantity || 0);

    return Boolean(product && size && qty > 0);
  };

  const isRecordReadyToConfirm = (record) => {
    const items = safeArray(record.items);

    return (
      record.status === "ready_to_create" &&
      record.purchase_order_no &&
      record.deliver_by &&
      items.length > 0 &&
      items.every(itemHasConfirmData)
    );
  };

  const getOrderItemsText = (record) => {
    const items = safeArray(record.items);
    if (!items.length) return "--";

    const rows = items
      .map((item) => {
        const size = getDisplaySize(item);
        const product = getDisplayProduct(item);
        const fabricColor = getDisplayFabricColor(item);
        const parts = [size, product, fabricColor].filter(Boolean);

        if (!parts.length) return null;

        return (
          <div key={item.id} style={{ marginBottom: 4 }}>
            {size ? <b>{size}</b> : null}
            {size && product ? " — " : null}
            {product || null}
            {fabricColor ? (
              <Text type="secondary">
                {(size || product) ? " — " : ""}
                {fabricColor}
              </Text>
            ) : null}
          </div>
        );
      })
      .filter(Boolean);

    return rows.length ? rows : "--";
  };

  const getOrderQty = (record) =>
    safeArray(record.items).reduce((sum, item) => sum + toNumber(item.quantity, 0), 0);

  const actionButton = (record) => {
    if (isRecordReadyToConfirm(record)) {
      return (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() => fetchDetails(record.id)}
        >
          Confirm
        </Button>
      );
    }

    if (record.status === "created") {
      return (
        <Button icon={<EyeOutlined />} onClick={() => fetchDetails(record.id)}>
          View
        </Button>
      );
    }

    return (
      <Button style={{ borderColor: "#faad14", color: "#ad6800" }} icon={<EyeOutlined />} onClick={() => fetchDetails(record.id)}>
        Review
      </Button>
    );
  };

  const tableColumns = [
    {
      title: "PO No",
      dataIndex: "purchase_order_no",
      width: 145,
      render: (value) => (
        <Text strong style={{ whiteSpace: "nowrap" }}>
          {value || <Text type="danger">Missing</Text>}
        </Text>
      ),
    },
    {
      title: "Customer Name",
      width: 180,
      render: (_, record) => (
        <Text style={{ whiteSpace: "nowrap" }}>
          {record.matchedUser?.company_name ||
            record.matchedUser?.full_name ||
            record.matchedUser?.email ||
            "--"}
        </Text>
      ),
    },
    {
      title: "Size / Product / Fabric Color",
      width: 430,
      render: (_, record) => (
        <div className="auto-order-item-cell">
          {getOrderItemsText(record)}
        </div>
      ),
    },
    {
      title: "QTY",
      width: 70,
      align: "center",
      render: (_, record) => (
        <Tag color="blue" style={{ margin: 0, minWidth: 34, textAlign: "center" }}>
          {getOrderQty(record) || "-"}
        </Tag>
      ),
    },
    {
      title: "Order Date",
      dataIndex: "order_date",
      width: 105,
      render: (value, record) => (
        <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
          {formatDate(value || record.email_date)}
        </Text>
      ),
    },
    {
      title: "Deliver By",
      dataIndex: "deliver_by",
      width: 105,
      render: (value) =>
        value ? (
          <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
            {formatDate(value)}
          </Text>
        ) : (
          <Text type="warning" style={{ whiteSpace: "nowrap" }}>
            Add date
          </Text>
        ),
    },
    {
      title: "Action",
      width: 155,
      align: "left",
      render: (_, record) => (
        <Space size={8} wrap={false}>
          {actionButton(record)}
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteAutoOrder(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 4 }}>
      <style>{`
        .auto-orders-compact-table .ant-table {
          border-radius: 12px;
          overflow: hidden;
        }

        .auto-orders-compact-table .ant-table-container {
          border: 1px solid #eef2f7;
          border-radius: 12px;
        }

        .auto-orders-compact-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #0f172a;
          font-weight: 700;
          padding: 12px 14px !important;
          white-space: nowrap;
          border-bottom: 1px solid #e5e7eb !important;
        }

        /* Keep every row content directly under the heading.
           This removes the visible blank band caused by tall multi-item rows. */
        .auto-orders-compact-table .ant-table-tbody > tr > td {
          padding: 8px 14px !important;
          vertical-align: top !important;
          border-bottom: 1px solid #f1f5f9 !important;
          height: auto !important;
        }

        .auto-orders-compact-table .ant-table-tbody > tr.ant-table-measure-row,
        .auto-orders-compact-table .ant-table-tbody > tr.ant-table-measure-row > td {
          display: none !important;
          height: 0 !important;
          padding: 0 !important;
          border: 0 !important;
        }

        .auto-orders-compact-table .ant-table-tbody > tr > td .ant-typography {
          line-height: 1.25;
        }

        .auto-orders-compact-table .ant-table-tbody > tr > td:first-child,
        .auto-orders-compact-table .ant-table-tbody > tr > td:nth-child(2),
        .auto-orders-compact-table .ant-table-tbody > tr > td:nth-child(4),
        .auto-orders-compact-table .ant-table-tbody > tr > td:nth-child(5),
        .auto-orders-compact-table .ant-table-tbody > tr > td:nth-child(6) {
          padding-top: 12px !important;
        }

        .auto-orders-compact-table .ant-table-tbody > tr:hover > td {
          background: #f8fbff !important;
        }

        .auto-orders-compact-table .ant-table-cell::before {
          display: none !important;
        }

        .auto-orders-compact-table .ant-table-body {
          scrollbar-width: thin;
        }

        .auto-order-item-cell {
          line-height: 1.45;
          min-width: 320px;
          max-width: 430px;
          padding-top: 0;
        }

        .auto-order-item-cell > div {
          margin-bottom: 2px !important;
        }

        .auto-order-item-cell > div:last-child {
          margin-bottom: 0 !important;
        }

        .auto-order-review-modal-wrap {
          z-index: 5000 !important;
        }

        .auto-order-review-modal-wrap .ant-modal-mask,
        .auto-order-review-modal-wrap .ant-modal-wrap {
          z-index: 5000 !important;
        }

        .auto-order-review-modal-wrap .ant-modal {
          max-width: calc(100vw - 32px) !important;
          margin-left: auto !important;
          margin-right: 24px !important;
          padding-bottom: 0 !important;
        }

        @media (min-width: 992px) {
          .auto-order-review-modal-wrap .ant-modal {
            width: min(1180px, calc(100vw - 300px)) !important;
          }
        }

        @media (max-width: 991px) {
          .auto-order-review-modal-wrap .ant-modal {
            width: calc(100vw - 24px) !important;
            margin: 12px auto !important;
          }
        }

        .auto-order-review-modal .ant-modal-content {
          max-height: calc(100vh - 32px);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border-radius: 14px;
        }

        .auto-order-review-modal .ant-modal-header,
        .auto-order-review-modal .ant-modal-footer {
          flex-shrink: 0;
        }

        .auto-order-review-modal .ant-modal-body {
          overflow-y: auto;
          overflow-x: hidden;
          max-height: calc(100vh - 170px);
        }

      `}</style>
      <Card
        style={{
          borderRadius: 16,
          boxShadow: "0 8px 28px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Title level={3} style={{ marginBottom: 4 }}>
              Auto Orders
            </Title>
            <Text type="secondary">
              Imported email orders are reviewed before final creation.
            </Text>
          </Col>

          <Col>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={syncing}
              onClick={() => syncEmails({ silent: false })}
            >
              Sync Emails
            </Button>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} md={8} lg={7}>
            <Input.Search
              allowClear
              placeholder="Search PO, customer or email"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onSearch={(value) =>
                fetchOrders({
                  status: statusFilter,
                  customerId: customerIdFilter,
                  searchText: value,
                  nextPage: 1,
                })
              }
            />
          </Col>

          <Col xs={24} md={6} lg={5}>
            <Select
              value={statusFilter}
              options={STATUS_FILTERS}
              style={{ width: "100%" }}
              onChange={(value) => {
                setStatusFilter(value);
                setPage(1);
                fetchOrders({
                  status: value,
                  customerId: customerIdFilter,
                  nextPage: 1,
                  searchText: search,
                });
              }}
            />
          </Col>

          <Col xs={24} md={6} lg={5}>
            <Select
              showSearch
              allowClear={false}
              value={customerIdFilter}
              options={customerFilterOptions}
              placeholder="Select Customer"
              style={{ width: "100%" }}
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value) => {
                const nextCustomerId = value || "all";

                setCustomerIdFilter(nextCustomerId);
                setPage(1);

                fetchOrders({
                  status: statusFilter,
                  customerId: nextCustomerId,
                  nextPage: 1,
                  searchText: search,
                });
              }}
            />
          </Col>

          <Col xs={24} md={4} lg={3}>
            <Button
              block
              icon={<ReloadOutlined />}
              onClick={() =>
                fetchOrders({
                  status: statusFilter,
                  customerId: customerIdFilter,
                  nextPage: 1,
                  searchText: search,
                })
              }
            >
              Refresh
            </Button>
          </Col>
        </Row>

        <Table
          rowKey="id"
          className="auto-orders-compact-table"
          size="small"
          tableLayout="fixed"
          columns={tableColumns}
          dataSource={sortedOrders}
          loading={loading}
          scroll={{ x: 1190 }}
          pagination={{
            current: page,
            pageSize: 20,
            total,
            showSizeChanger: false,
            onChange: (nextPage) =>
              fetchOrders({
                status: statusFilter,
                customerId: customerIdFilter,
                nextPage,
                searchText: search,
              }),
          }}
        />
      </Card>

      <Modal
        open={detailsOpen}
        onCancel={() => {
          setDetailsOpen(false);
          setPricePreview(DEFAULT_PRICE_PREVIEW);
        }}
        width={1180}
        zIndex={5000}
        centered={false}
        wrapClassName="auto-order-review-modal-wrap"
        className="auto-order-review-modal"
        style={{ top: 12 }}
        title={`Review Auto Order ${selectedOrder?.purchase_order_no || ""}`}
        destroyOnClose={false}
        footer={[
  <Button
    key="close"
    onClick={() => {
      setDetailsOpen(false);
      setPricePreview(DEFAULT_PRICE_PREVIEW);
    }}
  >
    Close
  </Button>,

  <Button
    key="confirm"
    type="primary"
    icon={<CheckCircleOutlined />}
    loading={creating || saving}
    disabled={!selectedOrder || selectedOrder.status === "created"}
    onClick={createFinalOrder}
  >
    Confirm
  </Button>,
]}
      >
        {selectedOrder && (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={8}>
                <Card size="small" title="Email Details">
                  <p>
                    <b>From:</b> {selectedOrder.from_name || "-"}{" "}
                    {selectedOrder.from_email || "-"}
                  </p>

                  <p>
                    <b>Sender Domain:</b> {extractSenderDomain(selectedOrder) || "-"}
                  </p>

                  <p>
                    <b>Customer Match:</b>{" "}
                    {selectedOrder.customer_match_reason || "-"}
                  </p>

                  {selectedOrder.error_message && (
                    <Alert
                      type="error"
                      showIcon
                      message={selectedOrder.error_message}
                      style={{ marginTop: 12 }}
                    />
                  )}
                </Card>

                <Card
                  size="small"
                  title="Pricing Summary"
                  loading={pricePreview.loading}
                  style={{ marginTop: 16 }}
                >
                  {pricePreview.error ? (
                    <Alert
                      type="error"
                      showIcon
                      message="Pricing preview failed"
                      description={pricePreview.error}
                    />
                  ) : pricePreview.summary ? (
                    <div style={{ lineHeight: 2 }}>
                      <div>
                        <b>Subtotal Ex VAT:</b>{" "}
                        {money(
                          pricePreview.summary.subtotalExVat ??
                            pricePreview.summary.subtotal_ex_vat ??
                            pricePreview.summary.subtotal
                        )}
                      </div>

                      <div>
                        <b>VAT 23%:</b>{" "}
                        {money(
                          pricePreview.summary.vatAmount ??
                            pricePreview.summary.vat_amount ??
                            pricePreview.summary.taxAmount ??
                            pricePreview.summary.tax_amount
                        )}
                      </div>

                      <div>
                        <b>Total Incl. VAT:</b>{" "}
                        {money(
                          pricePreview.summary.totalWithVat ??
                            pricePreview.summary.total_with_vat ??
                            pricePreview.summary.total
                        )}
                      </div>
                    </div>
                  ) : (
                    <Text type="secondary">
                      Select product, size, quantity and required bed options to calculate pricing.
                    </Text>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={16}>
                <Card
                  size="small"
                  title="Order Review"
                  bodyStyle={{ padding: 12 }}
                >
                  <Form form={form} layout="vertical">
                    <Row gutter={[10, 4]} align="top">
                      <Col xs={24} md={8}>
                        <Form.Item
                          label="Customer"
                          name="matched_user_id"
                          style={{ marginBottom: 8 }}
                          rules={[{ required: true, message: "Customer is required" }]}
                        >
                          <Select
                            showSearch
                            allowClear
                            size="small"
                            placeholder="Select customer"
                            options={customerOptions}
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={5}>
                        <Form.Item
                          label="PO Number"
                          name="purchase_order_no"
                          style={{ marginBottom: 8 }}
                          rules={[{ required: true, message: "PO number is required" }]}
                        >
                          <Input size="small" placeholder="PO number" />
                        </Form.Item>
                      </Col>

                      <Col xs={12} md={5}>
                        <Form.Item
                          label="Order Date"
                          name="order_date"
                          style={{ marginBottom: 8 }}
                        >
                          <Input size="small" type="date" />
                        </Form.Item>
                      </Col>

                      <Col xs={12} md={6}>
                        <Form.Item
                          label="Deliver By"
                          name="deliver_by"
                          style={{ marginBottom: 8 }}
                          rules={[{ required: true, message: "Deliver by date is required" }]}
                        >
                          <Input size="small" type="date" />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item
                          label="Notes"
                          name="review_notes"
                          style={{ marginBottom: 6 }}
                        >
                          <Input.TextArea
                            rows={1}
                            placeholder="Optional notes"
                            style={{ resize: "none" }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider style={{ margin: "10px 0" }} />

                    <Form.List name="items">
                      {(fields, { add, remove }) => (
                        <>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 10,
                              marginBottom: 10,
                            }}
                          >
                            <Space size={8}>
                              <Text strong>Items</Text>
                              <Tag color="blue" style={{ margin: 0 }}>
                                {fields.length}
                              </Tag>
                            </Space>

                            {selectedOrder?.status !== "created" && (
                              <Button
                                size="small"
                                type="dashed"
                                onClick={() =>
                                  add({
                                    productId: undefined,
                                    sizeId: undefined,
                                    fabricId: undefined,
                                    fabricColorId: undefined,
                                    storageType: undefined,
                                    quantity: 1,
                                    confirmed_sale_unit_price: undefined,
                                    admin_notes: "",
                                  })
                                }
                              >
                                + Add Item
                              </Button>
                            )}
                          </div>

                          {fields.map((field, index) => {
                            const watchedItem = watchedItems?.[field.name] || {};
                            const item = safeArray(selectedOrder.items)[index] || watchedItem || {};
                            const selectedProduct = findProductById(
                              options.products,
                              watchedItem.productId
                            );

                            const isBedItem = isBedProductName(
                              selectedProduct?.product_name ||
                                cleanExtractedProduct(item?.raw_product_text) ||
                                cleanExtractedProduct(item?.raw_description) ||
                                ""
                            );

                            const itemPreview = getPreviewItemPrice(
                              pricePreview.items,
                              index,
                              watchedItem?.id || item?.id
                            );

                            const linePrice = getLinePricingValues(itemPreview);

                            const extractedProduct =
                              cleanExtractedProduct(item?.raw_product_text) || "-";
                            const extractedSize =
                              cleanExtractedSize(item?.raw_size_text) || "-";
                            const extractedFabric =
                              cleanExtractedFabric(item?.raw_fabric_text) || "-";

                            return (
                              <Card
                                key={field.key}
                                size="small"
                                title={`Item ${index + 1}`}
                                bodyStyle={{ padding: 12 }}
                                style={{ marginBottom: 10 }}
                                extra={
                                  selectedOrder?.status !== "created" && fields.length > 1 ? (
                                    <Button
                                      danger
                                      size="small"
                                      type="link"
                                      onClick={() => remove(field.name)}
                                      style={{ padding: 0 }}
                                    >
                                      Remove
                                    </Button>
                                  ) : null
                                }
                              >
                                <Form.Item name={[field.name, "id"]} hidden>
                                  <Input />
                                </Form.Item>

                                <Form.Item
                                  name={[field.name, "confirmed_sale_unit_price"]}
                                  hidden
                                >
                                  <InputNumber />
                                </Form.Item>

                                <div
                                  style={{
                                    display: "flex",
                                    gap: 12,
                                    flexWrap: "wrap",
                                    fontSize: 12,
                                    padding: "7px 9px",
                                    marginBottom: 10,
                                    background: "#fafafa",
                                    border: "1px solid #f0f0f0",
                                    borderRadius: 8,
                                  }}
                                >
                                  <span>
                                    <b>Product:</b> {extractedProduct}
                                  </span>
                                  <span>
                                    <b>Size:</b> {extractedSize}
                                  </span>
                                  <span>
                                    <b>Fabric / Colour:</b> {extractedFabric}
                                  </span>
                                </div>

                                <Row gutter={[8, 2]}>
                                  <Col xs={24} md={6}>
                                    <Form.Item
                                      label="Product"
                                      name={[field.name, "productId"]}
                                      style={{ marginBottom: 8 }}
                                      rules={[
                                        { required: true, message: "Product is required" },
                                      ]}
                                    >
                                      <Select
                                        showSearch
                                        allowClear
                                        size="small"
                                        placeholder="Select product"
                                        options={productOptions}
                                        optionFilterProp="label"
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col xs={12} md={3}>
                                    <Form.Item
                                      label="Size"
                                      name={[field.name, "sizeId"]}
                                      style={{ marginBottom: 8 }}
                                      rules={[
                                        { required: true, message: "Size is required" },
                                      ]}
                                    >
                                      <Select
                                        showSearch
                                        allowClear
                                        size="small"
                                        placeholder="Size"
                                        options={sizeOptions}
                                        optionFilterProp="label"
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col xs={12} md={3}>
                                    <Form.Item
                                      label="Qty"
                                      name={[field.name, "quantity"]}
                                      style={{ marginBottom: 8 }}
                                      rules={[
                                        { required: true, message: "Qty is required" },
                                      ]}
                                    >
                                      <InputNumber
                                        min={1}
                                        size="small"
                                        style={{ width: "100%" }}
                                      />
                                    </Form.Item>
                                  </Col>

                                  {isBedItem && (
                                    <>
                                      <Col xs={24} md={4}>
                                        <Form.Item
                                          label="Storage"
                                          name={[field.name, "storageType"]}
                                          style={{ marginBottom: 8 }}
                                          rules={[
                                            {
                                              required: true,
                                              message: "Storage type is required for bed items",
                                            },
                                          ]}
                                        >
                                          <Select
                                            allowClear
                                            size="small"
                                            placeholder="Storage"
                                            options={(options.storageTypes || STORAGE_TYPES).map(
                                              (type) => ({
                                                value: type,
                                                label: type,
                                              })
                                            )}
                                          />
                                        </Form.Item>
                                      </Col>

                                      <Col xs={12} md={4}>
                                        <Form.Item
                                          label="Fabric"
                                          name={[field.name, "fabricId"]}
                                          style={{ marginBottom: 8 }}
                                          rules={[
                                            {
                                              required: true,
                                              message: "Fabric is required for bed items",
                                            },
                                          ]}
                                        >
                                          <Select
                                            showSearch
                                            allowClear
                                            size="small"
                                            placeholder="Fabric"
                                            options={fabricOptions}
                                            optionFilterProp="label"
                                          />
                                        </Form.Item>
                                      </Col>

                                      <Col xs={12} md={4}>
                                        <Form.Item
                                          label="Fabric Color"
                                          name={[field.name, "fabricColorId"]}
                                          style={{ marginBottom: 8 }}
                                          rules={[
                                            {
                                              required: true,
                                              message: "Fabric color is required for bed items",
                                            },
                                          ]}
                                        >
                                          <Select
                                            showSearch
                                            allowClear
                                            size="small"
                                            placeholder="Color"
                                            options={colorOptions}
                                            optionFilterProp="label"
                                          />
                                        </Form.Item>
                                      </Col>
                                    </>
                                  )}

                                  <Col span={24}>
                                    <div
                                      style={{
                                        padding: "8px 10px",
                                        marginTop: 2,
                                        marginBottom: 8,
                                        borderRadius: 8,
                                        background: itemPreview?.ok === false ? "#fff2f0" : "#f6ffed",
                                        border: `1px solid ${
                                          itemPreview?.ok === false ? "#ffccc7" : "#b7eb8f"
                                        }`,
                                      }}
                                    >
                                      {itemPreview?.ok === false ? (
                                        <Alert
                                          type="error"
                                          showIcon
                                          message="Pricing not found"
                                          description={
                                            itemPreview.message ||
                                            "Please check product, size, fabric, fabric color, storage type and product variation price."
                                          }
                                        />
                                      ) : (
                                        <Row gutter={[12, 4]} align="middle">
                                          <Col xs={12} md={4}>
                                            <Text type="secondary">Base</Text>
                                            <div>
                                              <b>{money(linePrice.baseSalePrice)}</b>
                                            </div>
                                          </Col>

                                          <Col xs={12} md={4}>
                                            <Text type="secondary">Fabric</Text>
                                            <div>
                                              <b>{money(linePrice.fabricSalePrice)}</b>
                                            </div>
                                          </Col>

                                          <Col xs={12} md={4}>
                                            <Text type="secondary">Storage</Text>
                                            <div>
                                              <b>{money(linePrice.storageSalePrice)}</b>
                                            </div>
                                          </Col>

                                          <Col xs={12} md={4}>
                                            <Text type="secondary">Unit Ex VAT</Text>
                                            <div>
                                              <b>{money(linePrice.unitExVat)}</b>
                                            </div>
                                          </Col>

                                          <Col xs={12} md={4}>
                                            <Text type="secondary">VAT 23%</Text>
                                            <div>
                                              <b>{money(linePrice.vat)}</b>
                                            </div>
                                          </Col>

                                          <Col xs={12} md={4}>
                                            <Text type="secondary">Total Inc. VAT</Text>
                                            <div>
                                              <b>{money(linePrice.lineTotal || linePrice.unitInclVat)}</b>
                                            </div>
                                          </Col>
                                        </Row>
                                      )}
                                    </div>
                                  </Col>

                                  <Col span={24}>
                                    <Form.Item
                                      label="Item Notes"
                                      name={[field.name, "admin_notes"]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input.TextArea
                                        rows={1}
                                        placeholder="Optional item note"
                                        style={{ resize: "none" }}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Card>
                            );
                          })}
                        </>
                      )}
                    </Form.List>
                  </Form>
                </Card>
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={10}>
                <Card size="small" title="Attachments">
                  {safeArray(selectedOrder.files).length ? (
                    safeArray(selectedOrder.files).map((file) => (
                      <div key={file.id} style={{ marginBottom: 8 }}>
                        {file.file_url ? (
                          <a href={file.file_url} target="_blank" rel="noreferrer">
                            {file.file_name}
                          </a>
                        ) : (
                          <Text>{file.file_name}</Text>
                        )}

                        {file.mime_type && (
                          <Tag style={{ marginLeft: 8 }}>{file.mime_type}</Tag>
                        )}
                      </div>
                    ))
                  ) : (
                    <Text type="secondary">No attachments found</Text>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={14}>
                <Card size="small" title="Original Email / PDF Text">
                  <div
                    style={{
                      maxHeight: 360,
                      overflow: "auto",
                      whiteSpace: "pre-wrap",
                      background: "#fafafa",
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    {selectedOrder.raw_text || "No original text available"}
                  </div>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AutoOrdersAdmin;